import { useState, useCallback } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { adminService } from '../services/adminService';
import { ApiError, ForbiddenError } from '@/lib/apiClient';
import type {
  AdminUser,
  CreateUserRequest,
  CreateUserResponse,
  UpdateUserRequest,
  UserRole,
  UserStatus,
} from '../types/admin.types';

export const useAdminUsers = () => {
  const { getAccessToken } = useAuth();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per-row operation loading states
  const [togglingUserId, setTogglingUserId] = useState<number | null>(null);
  const [updatingRoleUserId, setUpdatingRoleUserId] = useState<number | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdatingUser, setIsUpdatingUser] = useState(false);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);

  // Inline errors (shown as dismissable banners above the table, table stays visible)
  const [toggleAuthError, setToggleAuthError] = useState<string | null>(null);
  const [updateRoleError, setUpdateRoleError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);
  const clearToggleAuthError = useCallback(() => setToggleAuthError(null), []);
  const clearUpdateRoleError = useCallback(() => setUpdateRoleError(null), []);
  const clearDeleteError = useCallback(() => setDeleteError(null), []);
  const clearEditError = useCallback(() => setEditError(null), []);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = await getAccessToken();
      if (!token) return;
      const data = await adminService.getUsers(token);
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  /**
   * Creates a user and returns the minimal response (user_id, email, name).
   * Does NOT reload the list — caller is responsible for reloading after
   * the complete-profile step (save or skip).
   */
  const createUser = useCallback(async (data: CreateUserRequest): Promise<CreateUserResponse> => {
    setIsCreating(true);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('No access token');
      return await adminService.createUser(token, data);
    } finally {
      setIsCreating(false);
    }
  }, [getAccessToken]);

  /**
   * Updates user profile fields (name, country, division).
   * Does NOT reload the list — caller handles reload after modal closes.
   */
  const updateUser = useCallback(async (userId: number, data: UpdateUserRequest): Promise<void> => {
    setIsUpdatingUser(true);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('No access token');
      await adminService.updateUser(token, userId, data);
    } finally {
      setIsUpdatingUser(false);
    }
  }, [getAccessToken]);

  /**
   * Edits a user's profile fields and optionally their role.
   * Calls updateUser for profile fields and updateRole if the role changed.
   * Updates local state optimistically on success.
   */
  const editUser = useCallback(async (
    userId: number,
    data: UpdateUserRequest,
    newRol?: UserRole,
  ): Promise<void> => {
    setEditingUserId(userId);
    setEditError(null);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('No access token');

      const hasProfileChanges =
        data.name !== undefined ||
        data.country_id !== undefined ||
        data.division !== undefined ||
        data.job_title !== undefined ||
        data.language !== undefined ||
        data.function !== undefined ||
        data.scope !== undefined ||
        data.seniority !== undefined;
      if (hasProfileChanges) {
        await adminService.updateUser(token, userId, data);
      }
      if (newRol !== undefined) {
        await adminService.updateRole(token, userId, newRol);
      }

      setUsers((prev) =>
        prev.map((u) => {
          if (u.user_id !== userId) return u;
          return {
            ...u,
            ...(data.name !== undefined && { name: data.name }),
            ...(data.country_id !== undefined && { country_id: data.country_id }),
            user_meta: {
              ...u.user_meta,
              ...(data.division !== undefined && { division: data.division }),
              ...(data.job_title !== undefined && { job_title: data.job_title }),
              ...(data.language !== undefined && { language: data.language }),
              ...(data.function !== undefined && { function: data.function }),
              ...(data.scope !== undefined && { scope: data.scope }),
              ...(data.seniority !== undefined && { seniority: data.seniority }),
              ...(newRol !== undefined && { rol: newRol }),
            },
          };
        }),
      );
    } catch (err) {
      if (err instanceof ForbiddenError) {
        setEditError(err.message || 'Cannot edit this user.');
      } else {
        setEditError(err instanceof Error ? err.message : 'Failed to save changes.');
      }
      throw err; // re-throw so EditUserModal can detect failure
    } finally {
      setEditingUserId(null);
    }
  }, [getAccessToken]);

  const toggleAuthorization = useCallback(async (userId: number, status: UserStatus) => {
    setTogglingUserId(userId);
    setToggleAuthError(null);
    try {
      const token = await getAccessToken();
      if (!token) return;
      await adminService.toggleAuthorization(token, userId, status);
      setUsers((prev) =>
        prev.map((u) => (u.user_id === userId ? { ...u, status } : u)),
      );
    } catch (err) {
      if (err instanceof ForbiddenError) {
        setToggleAuthError(err.message || 'Cannot change authorization for this user.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to update authorization');
      }
    } finally {
      setTogglingUserId(null);
    }
  }, [getAccessToken]);

  const updateRole = useCallback(async (userId: number, rol: UserRole) => {
    setUpdatingRoleUserId(userId);
    setUpdateRoleError(null);
    try {
      const token = await getAccessToken();
      if (!token) return;
      await adminService.updateRole(token, userId, rol);
      setUsers((prev) =>
        prev.map((u) =>
          u.user_id === userId
            ? { ...u, user_meta: { ...u.user_meta, rol } }
            : u,
        ),
      );
    } catch (err) {
      if (err instanceof ForbiddenError) {
        setUpdateRoleError(err.message || 'Cannot change role for this user.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to update role');
      }
    } finally {
      setUpdatingRoleUserId(null);
    }
  }, [getAccessToken]);

  const deleteUser = useCallback(async (userId: number) => {
    setDeletingUserId(userId);
    setDeleteError(null);
    try {
      const token = await getAccessToken();
      if (!token) return;
      await adminService.deleteUser(token, userId);
      setUsers((prev) => prev.filter((u) => u.user_id !== userId));
    } catch (err) {
      if (err instanceof ForbiddenError) {
        setDeleteError(err.message || 'Cannot delete this user.');
      } else if (err instanceof ApiError && err.status === 404) {
        setDeleteError(err.message || 'User not found. They may have been removed already.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to delete user');
      }
    } finally {
      setDeletingUserId(null);
    }
  }, [getAccessToken]);

  return {
    users,
    isLoading,
    error,
    clearError,
    isCreating,
    isUpdatingUser,
    editingUserId,
    togglingUserId,
    updatingRoleUserId,
    deletingUserId,
    toggleAuthError,
    updateRoleError,
    deleteError,
    editError,
    clearToggleAuthError,
    clearUpdateRoleError,
    clearDeleteError,
    clearEditError,
    loadUsers,
    createUser,
    updateUser,
    editUser,
    toggleAuthorization,
    updateRole,
    deleteUser,
  };
};
